import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton } from "@mui/material";
import * as XLSX from "xlsx";
import axios from "../../utils/api.js";

const StarhotelDetails = () => {
  const [hotelData, setHotelData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStarhotelDetails = async () => {
      const token = localStorage.getItem("token");
      try {
        const response = await axios.get("/get-star-hotels", {
          headers: { Authorization: `Bearer ${token}` },
        });

        setHotelData(response.data);
      } catch (error) {
        console.error("Error fetching StarhotelDetails:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStarhotelDetails();
  }, []);

  // Function to auto-size columns based on the longest data entry
  const autoFitColumns = (jsonData) => {
    return Object.keys(jsonData[0] || {}).map((key) => ({
      wch:
        Math.max(
          key.length, // Minimum width should fit the header text
          ...jsonData.map((row) => (row[key] ? row[key].toString().length : 0)) // Longest cell
        ) + 5, // Add some extra padding
    }));
  };

  // Function to download Excel
  const handleDownloadExcel = () => {
    if (hotelData.length === 0) {
      alert("No data available to download.");
      return;
    }
    const basicHotelInfoData = hotelData.map((hotel, index) => ({
      "S.No": index + 1,
      "Hotel Name": hotel.basicHotelInfo?.hotelName || "N/A",
      "Hotel Type": hotel.basicHotelInfo?.hotelType || "N/A",
      "Hotel Chain": hotel.basicHotelInfo?.hotelChain || "N/A",
      "Total Floor Area (sq. ft.)":
        hotel.basicHotelInfo?.totalFloorArea || "N/A",
      "Total Floors": hotel.basicHotelInfo?.totalFloors || "N/A",
      "Total Rooms": hotel.basicHotelInfo?.totalRooms || "N/A",
      "Total Suites": hotel.basicHotelInfo?.totalSuites || "N/A",
      Accreditations: hotel.basicHotelInfo?.accreditations?.join(", ") || "N/A",
    }));

    const dataCollectionDetailsData = hotelData.map((hotel, index) => ({
      "S.No": index + 1,
      "Hotel Name": hotel.basicHotelInfo?.hotelName || "N/A",
      "Date of Data Collection": hotel.dataCollectionDetails
        ?.dateOfDataCollection
        ? new Date(
            hotel.dataCollectionDetails.dateOfDataCollection
          ).toLocaleDateString()
        : "N/A",
      "Data Collected By":
        hotel.dataCollectionDetails?.dataCollectedBy || "N/A",
      "Data Source": hotel.dataCollectionDetails?.dataSource || "N/A",
    }));

    const hotelInfrastructureData = hotelData.map((hotel, index) => ({
      "S.No": index + 1,
      "Hotel Name": hotel.basicHotelInfo?.hotelName || "N/A",
      "Standard Rooms":
        hotel.hotelInfrastructureGuestFlow?.standardRooms || "N/A",
      "Standard Room Cost":
        hotel.hotelInfrastructureGuestFlow?.standardRoomCost || "N/A",
      "Standard Room Occupancy":
        hotel.hotelInfrastructureGuestFlow?.standardRoomOccupancy || "N/A",
      "Deluxe Rooms": hotel.hotelInfrastructureGuestFlow?.deluxeRooms || "N/A",
      "Deluxe Room Cost":
        hotel.hotelInfrastructureGuestFlow?.deluxeRoomCost || "N/A",
      "Deluxe Room Occupancy":
        hotel.hotelInfrastructureGuestFlow?.deluxeRoomOccupancy || "N/A",
      Suites: hotel.hotelInfrastructureGuestFlow?.suites || "N/A",
      "Suite Cost": hotel.hotelInfrastructureGuestFlow?.suiteCost || "N/A",
      "Suite Occupancy":
        hotel.hotelInfrastructureGuestFlow?.suiteOccupancy || "N/A",
      "Banquet Halls":
        hotel.hotelInfrastructureGuestFlow?.banquetHallCount || "N/A",
      "Banquet Usage Rate":
        hotel.hotelInfrastructureGuestFlow?.banquetHallUsageRate || "N/A",
      "Avg Daily Guests":
        hotel.hotelInfrastructureGuestFlow?.avgDailyGuests || "N/A",
      Restaurants: hotel.hotelInfrastructureGuestFlow?.restaurantCount || "N/A",
      "Restaurant Footfall":
        hotel.hotelInfrastructureGuestFlow?.restaurantFootfall || "N/A",
      "Kitchen Cleaning Frequency":
        hotel.hotelInfrastructureGuestFlow?.kitchenCleaningFrequency || "N/A",
    }));

    const hygieneDisinfectionData = hotelData.map((hotel, index) => ({
      "S.No": index + 1,
      "Hotel Name": hotel.basicHotelInfo?.hotelName || "N/A",
      "Cleaning Disinfection Budget":
        hotel.hotelHygieneDisinfectionPractices?.cleaningDisinfectionBudget ||
        "N/A",
      "Cleaning Cost Distribution (Rooms)":
        hotel.hotelHygieneDisinfectionPractices
          ?.cleaningCostDistributionrooms || "N/A",
      "Cleaning Cost Distribution (Banquet Halls)":
        hotel.hotelHygieneDisinfectionPractices
          ?.cleaningCostDistributionbanquetHalls || "N/A",
      "Cleaning Cost Distribution (Restaurants)":
        hotel.hotelHygieneDisinfectionPractices
          ?.cleaningCostDistributionrestaurants || "N/A",
      "Cleaning Cost Distribution (Spa/Gym)":
        hotel.hotelHygieneDisinfectionPractices
          ?.cleaningCostDistributionspaGym || "N/A",
      "Cleaning Cost Distribution (Lobby/Common Areas)":
        hotel.hotelHygieneDisinfectionPractices
          ?.cleaningCostDistributionlobbyCommonAreas || "N/A",
      "Cleaning Cost Distribution (Kitchen)":
        hotel.hotelHygieneDisinfectionPractices
          ?.cleaningCostDistributionkitchen || "N/A",
      "Disinfection Techniques":
        hotel.hotelHygieneDisinfectionPractices?.disinfectionTechniques?.join(
          ", "
        ) || "N/A",
      "Average Disinfection Time":
        hotel.hotelHygieneDisinfectionPractices?.avgDisinfectionTime || "N/A",
      "Disinfection Staff Availability":
        hotel.hotelHygieneDisinfectionPractices
          ?.disinfectionStaffAvailability || "N/A",
      "Cleaning Challenges":
        hotel.hotelHygieneDisinfectionPractices?.cleaningChallenges?.join(
          ", "
        ) || "N/A",
    }));

    const healthSafetyComplianceData = hotelData.map((hotel, index) => ({
      "S.No": index + 1,
      "Hotel Name": hotel.basicHotelInfo?.hotelName || "N/A",
      "Health Safety Certified": hotel.guestHealthSafetyCompliance
        ?.healthSafetyCertified
        ? "Yes"
        : "No",
      "Disinfection Frequency (Per Room)":
        hotel.guestHealthSafetyCompliance?.disinfectionFrequencyPerRoom ||
        "N/A",
      "Disinfection Protocols":
        hotel.guestHealthSafetyCompliance?.disinfectionProtocols?.join(", ") ||
        "N/A",
      "Hygiene Audit Frequency":
        hotel.guestHealthSafetyCompliance?.hygieneAuditFrequency || "N/A",
      "Guest Hygiene Complaints": hotel.guestHealthSafetyCompliance
        ?.guestHygieneComplaints?.hasComplaints
        ? hotel.guestHealthSafetyCompliance?.guestHygieneComplaints?.details ||
          "Details Not Provided"
        : "No Complaints",
    }));

    const staffExposureAutomationData = hotelData.map((hotel, index) => ({
      "S.No": index + 1,
      "Hotel Name": hotel.basicHotelInfo?.hotelName || "N/A",
      "Housekeeping Staff Per Shift":
        hotel.staffExposureAutomationReadiness?.housekeepingStaffPerShift ||
        "N/A",
      "Avg Room Cleaning Time (mins)":
        hotel.staffExposureAutomationReadiness?.avgRoomCleaningTime || "N/A",
      "Staff Disinfection Complaints":
        hotel.staffExposureAutomationReadiness?.staffDisinfectionComplaints ||
        "N/A",
      "Chemical Exposure Reports":
        hotel.staffExposureAutomationReadiness?.chemicalExposureReports ||
        "N/A",
      "Current PPE Usage":
        hotel.staffExposureAutomationReadiness?.currentPPEUsage?.join(", ") ||
        "N/A",
      "Automation Readiness": hotel.staffExposureAutomationReadiness
        ?.automationReadiness
        ? "Yes"
        : "No",
    }));

    const timeCostEfficiencyUVCRobotData = hotelData.map((hotel, index) => ({
      "S.No": index + 1,
      "Hotel Name": hotel.basicHotelInfo?.hotelName || "N/A",
      "Manual Disinfection Time (mins/day)":
        hotel.timeCostEfficiencyUVCRobot?.manualDisinfectionTimePerDay || "N/A",
      "High-Risk Area Disinfection Time (mins)":
        hotel.timeCostEfficiencyUVCRobot?.highRiskAreaDisinfectionTime || "N/A",
      "Chemical Disinfection Dry Time (mins)":
        hotel.timeCostEfficiencyUVCRobot?.chemicalDisinfectionDryTime || "N/A",
      "Labour Cost (per month)":
        hotel.timeCostEfficiencyUVCRobot?.labourCostPerMonth || "N/A",
      "Disinfection Chemical Cost":
        hotel.timeCostEfficiencyUVCRobot?.disinfectionChemicalCost || "N/A",
      "Electricity Consumption (kWh)":
        hotel.timeCostEfficiencyUVCRobot?.electricityConsumption || "N/A",
      "Guest Refund Cost":
        hotel.timeCostEfficiencyUVCRobot?.guestRefundCost || "N/A",
    }));

    const aiUVCRobotFeasibilityData = hotelData.map((hotel, index) => ({
      "S.No": index + 1,
      "Hotel Name": hotel.basicHotelInfo?.hotelName || "N/A",
      "Interest in AI": hotel.aiUVCRobotFeasibility?.interestInAI
        ? "Yes"
        : "No",
      "Estimated Budget": hotel.aiUVCRobotFeasibility?.estimatedBudget || "N/A",
      "Financial Model": hotel.aiUVCRobotFeasibility?.financialModel || "N/A",
      "Customization Needs":
        hotel.aiUVCRobotFeasibility?.customizationNeeds.join(", ") || "N/A",
      "Integration Needs":
        hotel.aiUVCRobotFeasibility?.integrationNeeds.join(", ") || "N/A",
      "Safety Concerns":
        hotel.aiUVCRobotFeasibility?.safetyConcerns.join(", ") || "N/A",
    }));

    const requiredDemoNextStepsData = hotelData.map((hotel, index) => ({
      "S.No": index + 1,
      "Hotel Name": hotel.basicHotelInfo?.hotelName || "N/A",
      "Onsite Demo Interest": hotel.requiredDemonstrationNextSteps
        ?.onsiteDemoInterest
        ? "Yes"
        : "No",
      "Pilot Project Readiness": hotel.requiredDemonstrationNextSteps
        ?.pilotProjectReadiness
        ? "Yes"
        : "No",
      "Key Decision Maker": `${
        hotel.requiredDemonstrationNextSteps?.keyDecisionMaker?.name || "N/A"
      }\n${
        hotel.requiredDemonstrationNextSteps?.keyDecisionMaker?.contact || "N/A"
      }`,
      "Decision Making Timeline":
        hotel.requiredDemonstrationNextSteps?.decisionMakingTimeline || "N/A",
    }));

    const additionalNotesData = hotelData.map((hotel, index) => ({
      "S.No": index + 1,
      "Hotel Name": hotel.basicHotelInfo?.hotelName || "N/A",
      "Additional Notes": hotel.additionalnotes?.note || "N/A",
    }));

    const wb = XLSX.utils.book_new();

    // Convert JSON to Worksheet
    const ws1 = XLSX.utils.json_to_sheet(basicHotelInfoData);
    const ws2 = XLSX.utils.json_to_sheet(dataCollectionDetailsData);
    const ws3 = XLSX.utils.json_to_sheet(hotelInfrastructureData);
    const ws4 = XLSX.utils.json_to_sheet(hygieneDisinfectionData);
    const ws5 = XLSX.utils.json_to_sheet(healthSafetyComplianceData);
    const ws6 = XLSX.utils.json_to_sheet(staffExposureAutomationData);
    const ws7 = XLSX.utils.json_to_sheet(timeCostEfficiencyUVCRobotData);
    const ws8 = XLSX.utils.json_to_sheet(aiUVCRobotFeasibilityData);
    const ws9 = XLSX.utils.json_to_sheet(requiredDemoNextStepsData);
    const ws10 = XLSX.utils.json_to_sheet(additionalNotesData);

    // Auto-size columns based on content
    ws1["!cols"] = autoFitColumns(basicHotelInfoData);
    ws2["!cols"] = autoFitColumns(dataCollectionDetailsData);
    ws3["!cols"] = autoFitColumns(hotelInfrastructureData);
    ws4["!cols"] = autoFitColumns(hygieneDisinfectionData);
    ws5["!cols"] = autoFitColumns(healthSafetyComplianceData);
    ws6["!cols"] = autoFitColumns(staffExposureAutomationData);
    ws7["!cols"] = autoFitColumns(timeCostEfficiencyUVCRobotData);
    ws8["!cols"] = autoFitColumns(aiUVCRobotFeasibilityData);
    ws9["!cols"] = autoFitColumns(requiredDemoNextStepsData);
    ws10["!cols"] = autoFitColumns(additionalNotesData);

    // Set row height for better readability
    ws1["!rows"] = [{ hpx: 25 }];
    ws2["!rows"] = [{ hpx: 25 }];
    ws3["!rows"] = [{ hpx: 25 }];
    ws4["!rows"] = [{ hpx: 25 }];
    ws5["!rows"] = [{ hpx: 25 }];
    ws6["!rows"] = [{ hpx: 25 }];
    ws7["!rows"] = [{ hpx: 25 }];
    ws8["!rows"] = [{ hpx: 25 }];
    ws9["!rows"] = [{ hpx: 25 }];
    ws10["!rows"] = [{ hpx: 25 }];

    // Append sheets to workbook
    XLSX.utils.book_append_sheet(wb, ws1, "Hotel Info");
    XLSX.utils.book_append_sheet(wb, ws2, "Data Collection Details");
    XLSX.utils.book_append_sheet(wb, ws3, "Infrastructure & Guest Flow");
    XLSX.utils.book_append_sheet(wb, ws4, "Hygiene & Disinfection");
    XLSX.utils.book_append_sheet(wb, ws5, "Health & Safety Compliance");
    XLSX.utils.book_append_sheet(wb, ws6, "Staff Exposure & Automation");
    XLSX.utils.book_append_sheet(wb, ws7, "Time Cost Efficiency UVC Robot");
    XLSX.utils.book_append_sheet(wb, ws8, "AI UVC Robot Feasibility");
    XLSX.utils.book_append_sheet(wb, ws9, "Required Demo Next Steps");
    XLSX.utils.book_append_sheet(wb, ws10, "Additional Notes");

    // Get the current date
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const day = String(currentDate.getDate()).padStart(2, "0");

    // Format the date as dd-mm-yyyy
    const formattedDate = `${day}-${month}-${year}`;

    // Create the filename with the current date
    const filename = `StarHotel${formattedDate}.xlsx`;

    // Write and download Excel file
    XLSX.writeFile(wb, filename);
  };

  const pageStyle = {
    position: "relative",
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "24px 32px 48px",
    boxSizing: "border-box",
  };

  if (loading) {
    return (
      <div
        style={{
          ...pageStyle,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
        }}
      >
        <span style={{ color: "#64748b", fontSize: 15, fontWeight: 500 }}>
          Loading...
        </span>
      </div>
    );
  }
  return (
    <div style={pageStyle}>
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <div style={{ textAlign: "center", marginTop: 20, marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Star-Hotel Data
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          View and download star hotel data
        </p>
      </div>

      <button style={downloadButton} onClick={handleDownloadExcel}>
        📥 Download Excel
      </button>
      {hotelData.length === 0 ? (
        <div style={noDataStyle}>
          <h1>No Data Available</h1>
        </div>
      ) : (
        <>
          <h2 style={heading}>Basic Hotel Information</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hotel Name</th>
                  <th style={thStyle}>Hotel Type</th>
                  <th style={thStyle}>Hotel Chain</th>
                  <th style={thStyle}>Total Floor Area (sq. ft.)</th>
                  <th style={thStyle}>Total Floors</th>
                  <th style={thStyle}>Total Rooms</th>
                  <th style={thStyle}>Total Suites</th>
                  <th style={thStyle}>Accreditations</th>
                </tr>
              </thead>
              <tbody>
                {hotelData.map((hotel, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.hotelName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.hotelType || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.hotelChain || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.totalFloorArea || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.totalFloors || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.totalRooms || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.totalSuites || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.accreditations?.join(", ") || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Data Collection Details</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hotel Name</th>
                  <th style={thStyle}>Date of Data Collection</th>
                  <th style={thStyle}>Data Collected By</th>
                  <th style={thStyle}>Data Source</th>
                </tr>
              </thead>
              <tbody>
                {hotelData.map((hotel, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.hotelName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.dataCollectionDetails?.dateOfDataCollection
                        ? new Date(
                            hotel.dataCollectionDetails.dateOfDataCollection
                          ).toLocaleDateString("en-GB")
                        : "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.dataCollectionDetails?.dataCollectedBy}
                    </td>
                    <td style={tdStyle}>
                      {hotel.dataCollectionDetails?.dataSource || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Hotel Infrastructure & Guest Flow</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hotel Name</th>
                  <th style={thStyle}>Standard Rooms</th>
                  <th style={thStyle}>Standard Room Cost</th>
                  <th style={thStyle}>Standard Room Occupancy</th>
                  <th style={thStyle}>Deluxe Rooms</th>
                  <th style={thStyle}>Deluxe Room Cost</th>
                  <th style={thStyle}>Deluxe Room Occupancy</th>
                  <th style={thStyle}>Suites</th>
                  <th style={thStyle}>Suite Cost</th>
                  <th style={thStyle}>Suite Occupancy</th>
                  <th style={thStyle}>Banquet Halls</th>
                  <th style={thStyle}>Banquet Usage Rate</th>
                  <th style={thStyle}>Avg Daily Guests</th>
                  <th style={thStyle}>Restaurants</th>
                  <th style={thStyle}>Restaurant Footfall</th>
                  <th style={thStyle}>Kitchen Cleaning Frequency</th>
                </tr>
              </thead>
              <tbody>
                {hotelData.map((hotel, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.hotelName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelInfrastructureGuestFlow.standardRooms ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelInfrastructureGuestFlow.standardRoomCost ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelInfrastructureGuestFlow
                        .standardRoomOccupancy || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelInfrastructureGuestFlow.deluxeRooms || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelInfrastructureGuestFlow.deluxeRoomCost ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelInfrastructureGuestFlow.deluxeRoomOccupancy ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelInfrastructureGuestFlow.suites || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelInfrastructureGuestFlow.suiteCost || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelInfrastructureGuestFlow.suiteOccupancy ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelInfrastructureGuestFlow.banquetHallCount ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelInfrastructureGuestFlow
                        .banquetHallUsageRate || "N/A"}
                      %
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelInfrastructureGuestFlow.avgDailyGuests ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelInfrastructureGuestFlow.restaurantCount ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelInfrastructureGuestFlow.restaurantFootfall ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelInfrastructureGuestFlow
                        .kitchenCleaningFrequency || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Hotel Hygiene and Disinfection Practices</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hotel Name</th>
                  <th style={thStyle}>Cleaning Disinfection Budget</th>
                  <th style={thStyle}>Cleaning Cost Distribution (Rooms)</th>
                  <th style={thStyle}>
                    Cleaning Cost Distribution (Banquet Halls)
                  </th>
                  <th style={thStyle}>
                    Cleaning Cost Distribution (Restaurants)
                  </th>
                  <th style={thStyle}>Cleaning Cost Distribution (Spa/Gym)</th>
                  <th style={thStyle}>
                    Cleaning Cost Distribution (Lobby/Common Areas)
                  </th>
                  <th style={thStyle}>Cleaning Cost Distribution (Kitchen)</th>
                  <th style={thStyle}>Disinfection Techniques</th>
                  <th style={thStyle}>Average Disinfection Time</th>
                  <th style={thStyle}>Disinfection Staff Availability</th>
                  <th style={thStyle}>Cleaning Challenges</th>
                </tr>
              </thead>
              <tbody>
                {hotelData.map((hotel, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.hotelName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelHygieneDisinfectionPractices
                        .cleaningDisinfectionBudget || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelHygieneDisinfectionPractices
                        .cleaningCostDistributionrooms || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelHygieneDisinfectionPractices
                        .cleaningCostDistributionbanquetHalls || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelHygieneDisinfectionPractices
                        .cleaningCostDistributionrestaurants || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelHygieneDisinfectionPractices
                        .cleaningCostDistributionspaGym || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelHygieneDisinfectionPractices
                        .cleaningCostDistributionlobbyCommonAreas || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelHygieneDisinfectionPractices
                        .cleaningCostDistributionkitchen || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelHygieneDisinfectionPractices
                        .disinfectionTechniques.length > 0
                        ? hotel.hotelHygieneDisinfectionPractices.disinfectionTechniques.join(
                            ", "
                          )
                        : "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelHygieneDisinfectionPractices
                        .avgDisinfectionTime || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {
                        hotel.hotelHygieneDisinfectionPractices
                          .disinfectionStaffAvailability
                      }
                    </td>
                    <td style={tdStyle}>
                      {hotel.hotelHygieneDisinfectionPractices.cleaningChallenges.join(
                        ", "
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Guest Health & Safety Compliance</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hotel Name</th>
                  <th style={thStyle}>Health Safety Certified</th>
                  <th style={thStyle}>Disinfection Frequency (Per Room)</th>
                  <th style={thStyle}>Disinfection Protocols</th>
                  <th style={thStyle}>Hygiene Audit Frequency</th>
                  <th style={thStyle}>Guest Hygiene Complaints</th>
                </tr>
              </thead>
              <tbody>
                {hotelData.map((hotel, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.hotelName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.guestHealthSafetyCompliance.healthSafetyCertified
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.guestHealthSafetyCompliance
                        .disinfectionFrequencyPerRoom || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.guestHealthSafetyCompliance.disinfectionProtocols.join(
                        ", "
                      )}
                    </td>
                    <td style={tdStyle}>
                      {hotel.guestHealthSafetyCompliance
                        .hygieneAuditFrequency || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.guestHealthSafetyCompliance.guestHygieneComplaints
                        .hasComplaints
                        ? hotel.guestHealthSafetyCompliance
                            .guestHygieneComplaints.details ||
                          "Details Not Provided"
                        : "No Complaints"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Staff Exposure & Automation Readiness</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hotel Name</th>
                  <th style={thStyle}>Housekeeping Staff Per Shift</th>
                  <th style={thStyle}>Avg Room Cleaning Time (mins)</th>
                  <th style={thStyle}>Staff Disinfection Complaints</th>
                  <th style={thStyle}>Chemical Exposure Reports</th>
                  <th style={thStyle}>Current PPE Usage</th>
                  <th style={thStyle}>Automation Readiness</th>
                </tr>
              </thead>
              <tbody>
                {hotelData.map((hotel, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.hotelName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.staffExposureAutomationReadiness
                        .housekeepingStaffPerShift || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.staffExposureAutomationReadiness
                        .avgRoomCleaningTime || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.staffExposureAutomationReadiness
                        .staffDisinfectionComplaints || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.staffExposureAutomationReadiness
                        .chemicalExposureReports || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.staffExposureAutomationReadiness.currentPPEUsage.join(
                        ", "
                      )}
                    </td>
                    <td style={tdStyle}>
                      {hotel.staffExposureAutomationReadiness
                        .automationReadiness
                        ? "Yes"
                        : "No"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Time & Cost Efficiency of UVC Robot</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hotel Name</th>
                  <th style={thStyle}>Manual Disinfection Time (mins/day)</th>
                  <th style={thStyle}>
                    High-Risk Area Disinfection Time (mins)
                  </th>
                  <th style={thStyle}>Chemical Disinfection Dry Time (mins)</th>
                  <th style={thStyle}>Labour Cost (per month)</th>
                  <th style={thStyle}>Disinfection Chemical Cost</th>
                  <th style={thStyle}>Electricity Consumption (kWh)</th>
                  <th style={thStyle}>Guest Refund Cost</th>
                </tr>
              </thead>
              <tbody>
                {hotelData.map((hotel, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.hotelName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.timeCostEfficiencyUVCRobot
                        .manualDisinfectionTimePerDay || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.timeCostEfficiencyUVCRobot
                        .highRiskAreaDisinfectionTime || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.timeCostEfficiencyUVCRobot
                        .chemicalDisinfectionDryTime || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.timeCostEfficiencyUVCRobot.labourCostPerMonth ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.timeCostEfficiencyUVCRobot
                        .disinfectionChemicalCost || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.timeCostEfficiencyUVCRobot
                        .electricityConsumption || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.timeCostEfficiencyUVCRobot.guestRefundCost ||
                        "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>AI UVC Robot Feasibility</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hotel Name</th>
                  <th style={thStyle}>Interest in AI</th>
                  <th style={thStyle}>Estimated Budget</th>
                  <th style={thStyle}>Financial Model</th>
                  <th style={thStyle}>Customization Needs</th>
                  <th style={thStyle}>Integration Needs</th>
                  <th style={thStyle}>Safety Concerns</th>
                </tr>
              </thead>
              <tbody>
                {hotelData.map((hotel, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.hotelName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.aiUVCRobotFeasibility.interestInAI ? "Yes" : "No"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.aiUVCRobotFeasibility.estimatedBudget || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.aiUVCRobotFeasibility.financialModel}
                    </td>
                    <td style={tdStyle}>
                      {hotel.aiUVCRobotFeasibility.customizationNeeds.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.aiUVCRobotFeasibility.integrationNeeds.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.aiUVCRobotFeasibility.safetyConcerns.join(", ") ||
                        "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Required Demonstration Next Steps</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hotel Name</th>
                  <th style={thStyle}>Onsite Demo Interest</th>
                  <th style={thStyle}>Pilot Project Readiness</th>
                  <th style={thStyle}>Key Decision Maker</th>
                  <th style={thStyle}>Decision Making Timeline</th>
                </tr>
              </thead>
              <tbody>
                {hotelData.map((hotel, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.hotelName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.requiredDemonstrationNextSteps.onsiteDemoInterest
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.requiredDemonstrationNextSteps
                        .pilotProjectReadiness
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {
                        hotel.requiredDemonstrationNextSteps.keyDecisionMaker
                          .name
                      }
                      <br />
                      {
                        hotel.requiredDemonstrationNextSteps.keyDecisionMaker
                          .contact
                      }
                    </td>
                    <td style={tdStyle}>
                      {
                        hotel.requiredDemonstrationNextSteps
                          .decisionMakingTimeline
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>* Additional Notes *</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hotel Name</th>
                  <th style={thStyle}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {hotelData.map((hotel, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {hotel.basicHotelInfo.hotelName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hotel.additionalnotes.note || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// Internal CSS styles
const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: "10px",
};

const thStyle = {
  backgroundColor: "#007bff",
  color: "#fff",
  padding: "10px",
  textAlign: "center",
  border: "1px solid #ddd",
};
const tableContainerStyle = {
  width: "100%",
  overflowX: "auto", // Enables horizontal scrolling
};
const tdStyle = {
  padding: "10px",
  border: "1px solid #ddd",
  textAlign: "center",
};

const heading = {
  fontSize: "24px",
  color: "black",
};

const downloadButton = {
  position: "absolute",
  top: "20px",
  right: "20px",
  backgroundColor: "#007bff",
  color: "white",
  border: "none",
  padding: "10px 15px",
  cursor: "pointer",
  borderRadius: "5px",
  fontSize: "14px",
};
const noDataStyle = {
  textAlign: "center",
  marginTop: "20px",
  fontSize: "20px",
  color: "#888",
};

export default StarhotelDetails;
